import React, { useMemo, useState } from 'react';
import { Box, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { SpatialOperationsShell } from '../../components/spatialOperations/SpatialOperationsShell';
import { CanonicalSafetyScanReport } from '../../components/safetyScanReport/CanonicalSafetyScanReport';
import { presentCanonicalSafetyScanReport } from '../../domain/safetyScanReport/canonicalSafetyScanReportPresenter';
import { CAMERA_MEASUREMENT_GOLDENS } from '../../domain/cameraMeasurement/cameraMeasurementGoldens';
import type { SpatialOperationsMode } from '../../domain/spatialOperations/spatialOperationsUrl';
import { parseSpatialOperationsMode } from '../../domain/spatialOperations/spatialOperationsUrl';
import type { SpatialOperationsStageId } from '../../domain/spatialOperations/spatialOperationsPresentation';
import {
  buildSpatialOperationsFixtureProps,
  FULL_LIFECYCLE_SCAN_MEASUREMENT,
  FULL_LIFECYCLE_SCAN_WINDOW,
  parseFixtureScenario,
} from './spatialOperationsFixtureModels';

function SpatialOperationsScanFixture({ onBack }: { onBack: () => void }) {
  const view = presentCanonicalSafetyScanReport({
    displayedReportId: 'after-1',
    persistedReport: {
      window_start_utc: FULL_LIFECYCLE_SCAN_WINDOW.start,
      window_end_utc: FULL_LIFECYCLE_SCAN_WINDOW.end,
      operating_condition: 'Walkway activity after Control.',
      recurrence_headline: 'Repeats in this Scan window',
      confirmed_reviewed_observations: FULL_LIFECYCLE_SCAN_MEASUREMENT.confirmed,
      usable_hours: FULL_LIFECYCLE_SCAN_MEASUREMENT.usableHours,
      rates_available: true,
      overall_rate: FULL_LIFECYCLE_SCAN_MEASUREMENT.rate,
    },
    locationLabel: 'Room B walkway zone',
    conditionRaw: 'Forklift-pedestrian proximity',
    verification: {
      report_status: 'verified',
      state: 'FROZEN',
      window: FULL_LIFECYCLE_SCAN_WINDOW,
      progress: {
        total: FULL_LIFECYCLE_SCAN_MEASUREMENT.reviewed,
        confirmed: FULL_LIFECYCLE_SCAN_MEASUREMENT.confirmed,
        cleared: 0,
        unresolved: 0,
        unreviewed: 0,
        complete: true,
      },
    },
    reconciliation: {
      verification: {
        total: FULL_LIFECYCLE_SCAN_MEASUREMENT.reviewed,
        confirmed: FULL_LIFECYCLE_SCAN_MEASUREMENT.confirmed,
        cleared: 0,
        unresolved: 0,
        unreviewed: 0,
        complete: true,
      },
      measurement: {
        numerator: { value: FULL_LIFECYCLE_SCAN_MEASUREMENT.confirmed },
        denominator: { usable_hours: FULL_LIFECYCLE_SCAN_MEASUREMENT.usableHours, proven: true },
        rate: { value: FULL_LIFECYCLE_SCAN_MEASUREMENT.rate, issuable: true, withheld_reasons: [] },
        gates: { coverage_sufficient: true, rate_issuable: true },
      },
    },
    lifecycleStory: {
      alertsLinked: true,
      controlRecorded: true,
      sinceAvailable: true,
      comparisonAvailable: true,
      resultDirection: 'decreased',
    },
  });
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f9', p: { xs: 1.5, md: 2 } }} data-testid="spatial-operations-interactive-scan">
      <Button size="small" onClick={onBack} data-testid="spatial-operations-scan-back" sx={{ mb: 1.5 }}>
        Back to Spatial Operations
      </Button>
      <CanonicalSafetyScanReport
        view={view}
        measurementSource={{
          siteId: 'yyz',
          deviceId: 'edge-1',
          cameraId: 'cam_walkway',
          windowStart: FULL_LIFECYCLE_SCAN_WINDOW.start,
          windowEnd: FULL_LIFECYCLE_SCAN_WINDOW.end,
        }}
        measurementOverride={CAMERA_MEASUREMENT_GOLDENS.measured}
      />
    </Box>
  );
}

/** Dev-only fixture host. Not a production data path. */
const SpatialOperationsFixturePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const scenario = parseFixtureScenario(location.search);
  const params = new URLSearchParams(location.search.startsWith('?') ? location.search.slice(1) : location.search);
  const [mode, setMode] = useState<SpatialOperationsMode>(parseSpatialOperationsMode(params.get('mode')));
  const [conditionId, setConditionId] = useState('cond-forklift');
  const [stage, setSelectedStage] = useState<SpatialOperationsStageId>('find');
  const [scanOpen, setScanOpen] = useState(params.get('view') === 'scan');
  const base = useMemo(() => buildSpatialOperationsFixtureProps(scenario), [scenario]);
  const selectedCondition = base.conditions.find((item) => item.conditionId === conditionId) || base.conditions[0];

  if (scanOpen) {
    return <SpatialOperationsScanFixture onBack={() => setScanOpen(false)} />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f9' }} data-testid="spatial-operations-fixture">
      <SpatialOperationsShell
        {...base}
        mode={mode}
        selectedCondition={selectedCondition}
        selectedStage={stage}
        onNavigate={(path) => {
          if (/\/acceptance\/scans\/[^/]+\/verify/.test(path)) {
            setScanOpen(true);
            return;
          }
          navigate(path);
        }}
        onSelectCamera={() => undefined}
        onSelectCondition={(next) => setConditionId(next)}
        onSelectMode={setMode}
        onSelectStage={setSelectedStage}
      />
    </Box>
  );
};

export default SpatialOperationsFixturePage;
